import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { NbButtonModule, NbCardModule, NbDialogRef, NbIconModule, NbSpinnerModule } from '@nebular/theme';
import { finalize } from 'rxjs/operators';
import { AlunoFrequencia } from '../../core/api/aluno-frequencia';
import { FrequenciaService } from '../../core/api/frequencia.service';
import { mensagemDeErro } from '../../utils/mensagem-erro';
import {
  carregarMensagemFaltas,
  linkWhatsapp,
  renderizarMensagemFaltas,
} from '../../utils/mensagem-whatsapp';

export type ModoChamadaPessoas = 'presentes' | 'faltantes';

/**
 * Popup com quem esteve presente, ou quem faltou, num dia de chamada.
 *
 * Fonte: `GET /frequencia/{turmaId}/{data}` — a mesma chamada da tela de frequência. A
 * listagem de chamadas só traz os totais, e o total não diz **quem**.
 *
 * Na lista de faltantes cada linha traz o botão de WhatsApp, com o mesmo texto editável das
 * faltas consolidadas (`localStorage`, um template para todas as turmas).
 */
@Component({
  selector: 'app-chamada-pessoas-dialog',
  standalone: true,
  imports: [CommonModule, NbCardModule, NbButtonModule, NbIconModule, NbSpinnerModule],
  template: `
    <nb-card class="dialog-alert chamada-pessoas-dialog">
      <nb-card-header>
        <span class="titulo">{{ modo === 'presentes' ? 'Presentes' : 'Faltantes' }} em {{ dataBr }}</span>
        <span class="subtitulo" *ngIf="turmaNome">{{ turmaNome }}</span>
      </nb-card-header>

      <nb-card-body>
        <div class="carregando" *ngIf="carregando">
          <nb-spinner status="primary" size="medium"></nb-spinner>
          <span>Carregando a chamada…</span>
        </div>

        <p class="erro" *ngIf="erro">{{ erro }}</p>

        <ng-container *ngIf="!carregando && !erro">
          <p class="resumo">
            <strong>{{ pessoas.length }}</strong>
            {{ resumo }}
          </p>

          <ul class="lista-pessoas" *ngIf="pessoas.length">
            <li *ngFor="let p of pessoas">
              <span class="pessoa-nome">{{ p.pessoaNome }}</span>

              <a
                *ngIf="modo === 'faltantes' && linkZap(p)"
                nbButton
                size="small"
                status="success"
                appearance="outline"
                [href]="linkZap(p)"
                target="_blank"
                rel="noopener"
                [attr.aria-label]="'Falar com ' + p.pessoaNome + ' no WhatsApp'"
                title="Falar no WhatsApp"
              >
                <nb-icon pack="eva" icon="message-circle-outline"></nb-icon>
                <span>WhatsApp</span>
              </a>

              <span class="sem-zap" *ngIf="modo === 'faltantes' && !linkZap(p)" title="Sem celular cadastrado">
                sem celular
              </span>
            </li>
          </ul>

          <p class="vazio" *ngIf="!pessoas.length">{{ mensagemVazia }}</p>
        </ng-container>
      </nb-card-body>

      <nb-card-footer class="footer-actions">
        <button nbButton status="primary" size="medium" type="button" (click)="fechar()">Fechar</button>
      </nb-card-footer>
    </nb-card>
  `,
  styles: [
    `
      .chamada-pessoas-dialog nb-card-header {
        display: flex;
        flex-direction: column;
        gap: 0.2rem;
      }

      .titulo {
        font-weight: 600;
        line-height: 1.3;
        word-break: break-word;
      }

      .subtitulo {
        font-size: 0.82rem;
        font-weight: 400;
        color: var(--text-hint-color, #8f9bb3);
        line-height: 1.35;
        word-break: break-word;
      }

      .carregando {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 1rem 0;
        color: var(--text-hint-color, #8f9bb3);
      }

      .erro {
        color: #db2c66;
        margin: 0;
      }

      .resumo {
        margin: 0 0 0.75rem;
        color: var(--text-hint-color, #8f9bb3);
      }

      .lista-pessoas {
        list-style: none;
        margin: 0;
        padding: 0;
        max-height: min(420px, 55vh);
        overflow-y: auto;
      }

      .lista-pessoas li {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
        padding: 0.5rem 0;
        border-bottom: 1px solid var(--divider-color, #edf1f7);
      }

      .lista-pessoas li:last-child {
        border-bottom: 0;
      }

      .pessoa-nome {
        min-width: 0;
        word-break: break-word;
      }

      .sem-zap {
        flex: 0 0 auto;
        font-size: 0.78rem;
        color: var(--text-hint-color, #8f9bb3);
      }

      .vazio {
        margin: 0;
        color: var(--text-hint-color, #8f9bb3);
      }
    `,
  ],
})
export class ChamadaPessoasDialogComponent implements OnInit {
  @Input() turmaId = '';
  @Input() turmaNome?: string;
  /** Data da chamada, `yyyy-MM-dd`. */
  @Input() data = '';
  @Input() modo: ModoChamadaPessoas = 'presentes';

  pessoas: AlunoFrequencia[] = [];
  carregando = false;
  erro: string | null = null;

  private readonly mensagem = carregarMensagemFaltas();

  constructor(
    private readonly ref: NbDialogRef<ChamadaPessoasDialogComponent>,
    private readonly frequenciaService: FrequenciaService,
  ) {}

  ngOnInit(): void {
    if (!this.turmaId || !this.data) {
      this.erro = 'Chamada sem turma ou sem data.';
      return;
    }

    this.carregando = true;
    this.frequenciaService
      .carregarFrequencia(this.turmaId, this.data)
      .pipe(finalize(() => (this.carregando = false)))
      .subscribe({
        next: (f) => {
          const alunos = f.alunos ?? [];
          this.pessoas = alunos
            .filter((a) => (this.modo === 'presentes' ? a.presente === true : a.presente !== true))
            .sort((a, b) => (a.pessoaNome ?? '').localeCompare(b.pessoaNome ?? '', 'pt-BR', { sensitivity: 'base' }));
        },
        error: (err) => {
          this.pessoas = [];
          this.erro = mensagemDeErro(err, 'Não foi possível carregar a chamada deste dia.');
        },
      });
  }

  get dataBr(): string {
    const partes = (this.data ?? '').split('-');
    return partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : this.data;
  }

  get resumo(): string {
    const um = this.pessoas.length === 1;
    if (this.modo === 'presentes') return um ? 'presente' : 'presentes';
    return um ? 'faltante' : 'faltantes';
  }

  get mensagemVazia(): string {
    return this.modo === 'presentes'
      ? 'Ninguém foi marcado como presente neste dia.'
      : 'Ninguém faltou neste dia.';
  }

  linkZap(p: AlunoFrequencia): string | null {
    return linkWhatsapp(
      p.pessoaTelefone,
      renderizarMensagemFaltas(this.mensagem, {
        nome: p.pessoaNome,
        turma: this.turmaNome,
        ultimaData: this.dataBr,
      }),
    );
  }

  fechar(): void {
    this.ref.close();
  }
}
